import { AssetGroup } from '@play-co/timestep-core';
import { View } from '@play-co/timestep-core/ui';
import DreamDriftHeroSelectionCard from 'src/game/dreamDrift/components/DreamDriftHeroSelectionCard';
import { dreamDriftCostumeAssets } from 'src/game/dreamDrift/dreamDrift.costumes';
import type { DreamDriftHeroSelectionMode } from 'src/game/dreamDrift/popups/PopupDreamDriftHeroSelection';
import { getPuzzleMap } from 'src/game/minigame/match2-timestep/match2.assets';
import { Match2GoalsSummary } from 'src/game/minigame/match2-timestep/components/Match2GoalsSummary';
import PopupMatch2Base, {
  type PopupMatch2BaseInitOptions,
} from 'src/game/minigame/match2-timestep/components/PopupMatch2Base';
import type { MapDef } from 'src/game/minigame/match2-timestep/game/defs/map';
import { getGoalsPath } from 'src/game/minigame/match2-timestep/game/util/assets';
import i18n from 'src/lib/i18n/i18n';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import { captureGenericError } from 'src/lib/sentry';
import sounds from 'src/lib/sounds';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import uiConfig from 'src/lib/ui/config';
import type { DreamDriftHero } from 'src/replicant/dreamDrift/dreamDrift.ruleset';

/** Arguments used to configure the Dream Drift pre-game confirmation. */
export interface PopupDreamDriftPregameInitOptions extends PopupMatch2BaseInitOptions {
  level: number;
  puzzleLevel: number;
  selectedHero: DreamDriftHero;
  selectionMode: DreamDriftHeroSelectionMode;
}

/** Close callback returning the hero displayed when Play was confirmed. */
type PopupDreamDriftPregameClose = (hero?: DreamDriftHero) => void;

/** Match-2 pre-game popup specialized for Dream Drift hero confirmation. */
export default class PopupDreamDriftPregame extends PopupMatch2Base<PopupDreamDriftPregameClose> {
  /** Match-2 goals associated with the current Dream Drift node. */
  private readonly goals: Match2GoalsSummary;

  /** Button that opens the complete hero-selection experience. */
  private readonly changeButton: ButtonScaleViewWithText;

  /** Animated compact card displaying the currently confirmed hero. */
  private heroCard?: DreamDriftHeroSelectionCard;

  /** Hero that will be returned if the player confirms Play. */
  private selectedHero?: DreamDriftHero;

  /** Lifecycle copy used when reopening the hero-selection popup. */
  private selectionMode: DreamDriftHeroSelectionMode = 'continue';

  /**
   * Create the shared Match-2 popup frame and Dream Drift-specific controls.
   *
   * @param opts Popup parent and typed close callback supplied by the registry.
   */
  constructor(opts: { superview: View; close: PopupDreamDriftPregameClose }) {
    // Reuse the standard Match-2 panel, title, close button, and Play action.
    super({
      superview: opts.superview,
      close: () => opts.close(undefined),
      height: 590,
      zIndex: 10001,
      title: {
        type: 'banner',
        color: 'green',
        textOffsetY: 22,
      },
      cta: {
        localeText: () => i18n('match2.ui.play').toLocaleUpperCase(),
        onClick: async () => {
          // Ignore an impossible confirmation before popup initialization finishes.
          if (!this.selectedHero) return;

          // Return the hero visible at the moment Play was confirmed.
          opts.close(this.selectedHero);
        },
        positioning: 'inside',
        offsetY: 5,
      },
      tracking: {
        feature: 'dream_drift',
        $subFeature: 'dream_drift_pregame',
      },
    });

    // Place the current puzzle goals between hero confirmation and Play.
    this.goals = new Match2GoalsSummary({
      superview: this.panel,
      x: this.panel.style.width * 0.5,
      y: 385,
    });

    // Reuse the standard secondary skin for the compact Change action.
    this.changeButton = new ButtonScaleViewWithText({
      superview: this.panel,
      ...uiConfig.buttons.secondary,
      width: 210,
      height: 78,
      x: 310,
      y: 145,
      icon: {
        image: 'assets/ui/dreamDrift/common/icon_change.png',
        width: 42,
        height: 42,
        offset: { x: -65, y: -4 },
      },
      labelOffsetX: 20,
      labelOffsetY: -2,
      font: 'Body',
      fontSize: 30,
      localeText: () => i18n('dreamDrift.pregame.change'),
      tracking: {
        feature: 'dream_drift',
        $subFeature: 'dream_drift_pregame_change',
      },
      onClick: async () => {
        // Ignore an impossible change request before popup initialization finishes.
        if (!this.selectedHero) return;

        // Keep the confirmation popup inert while Hero Selection is active above it.
        this.setInteractivity(false);
        sounds.sfx.play('ok');

        // Let the player change the hero without leaving the pre-game flow.
        const selectedHero = await openPopupPromise(
          'popupDreamDriftHeroSelection',
          {
            mode: this.selectionMode,
            selectedHero: this.selectedHero,
          },
        );

        // Refresh the animated summary with the newly confirmed hero.
        this.setHero(selectedHero);
        this.setInteractivity(true);
      },
    });
  }

  /**
   * Load the Dream Drift card, costume artwork, and Match-2 goal icons.
   *
   * @returns Asset group required before the popup begins its opening animation.
   */
  protected override getAssetsGroup(): AssetGroup {
    // Load every hero because Change can return any configured selection.
    return new AssetGroup([
      ...AssetGroup.constructURLs([
        'assets/ui/dreamDrift/common',
        'assets/ui/dreamDrift/popupHeroSelection',
        getGoalsPath(),
      ]),
      ...dreamDriftCostumeAssets,
    ]);
  }

  /**
   * Configure the Dream Drift node, underlying puzzle goals, and selected hero.
   *
   * @param opts Current node, puzzle, selection lifecycle, and hero identity.
   * @returns Promise resolved after applying the puzzle goals and starting the opening.
   */
  public override async init({
    level,
    puzzleLevel,
    selectedHero,
    selectionMode,
    useClickOutside,
  }: PopupDreamDriftPregameInitOptions): Promise<void> {
    // Display the Dream Drift progression node rather than its Match-2 map id.
    this.setTitleText(() =>
      i18n('match2.ui.level', { level }).toLocaleUpperCase(),
    );

    // Retain the lifecycle used by subsequent Change actions.
    this.selectionMode = selectionMode;
    this.setHero(selectedHero);

    // Resolve the puzzle goals before the popup can become visible.
    await this.updateGoals(puzzleLevel);

    // Open the popup after its artwork and goal icons finish preloading.
    super.init({ useClickOutside });
  }

  /**
   * Keep every Dream Drift pre-game action synchronized with popup interactivity.
   *
   * @param interactable Whether the popup should currently accept input.
   */
  public override setInteractivity(interactable: boolean): void {
    // Apply the standard state to Play and Close first.
    super.setInteractivity(interactable);

    // Apply the same state to the Dream Drift-specific Change action.
    this.changeButton?.updateOpts({ canHandleEvents: interactable });
  }

  /**
   * Create or update the animated summary card with a newly confirmed hero.
   *
   * @param hero Hero that should be displayed and returned by Play.
   */
  private setHero(hero: DreamDriftHero): void {
    // Store the hero used by both Change and Play.
    this.selectedHero = hero;

    // Reuse the existing animated pet and card hierarchy after first setup.
    if (this.heroCard) {
      this.heroCard.setHero(hero);
      return;
    }

    // Create the compact animated hero portrait only for initial setup.
    this.heroCard = new DreamDriftHeroSelectionCard({
      superview: this.panel,
      hero,
      presentation: 'summary',
      x: 45,
      y: 65,
      scale: 0.68,
    });
    this.heroCard.setSelected(true);
  }

  /**
   * Resolve and display the goal summary for the underlying Match-2 puzzle.
   *
   * @param puzzleLevel Match-2 puzzle map assigned to the Dream Drift node.
   * @returns Promise resolved after available puzzle-map data is applied.
   */
  private async updateGoals(puzzleLevel: number): Promise<void> {
    // Attempt to load the puzzle map without blocking the popup on bad data.
    let map: MapDef | null = null;
    try {
      map = await getPuzzleMap(puzzleLevel);
    } catch (error) {
      captureGenericError('DreamDriftPreGameError', error);
    }

    // A missing map can still proceed because gameplay will validate it again.
    if (!map) return;

    // Display the current Dream Drift puzzle objectives.
    this.goals.setGoals(map.goals);
  }
}
